import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskCategoryRequest, TaskCategoryResponse } from '../../../models/task.model';

@Component({
  selector: 'app-task-categories-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './task-categories-form.component.html',
})
export class TaskCategoriesFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TaskCategoryResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  readonly COLOR_PRESETS = [
    '#EF4444', '#F97316', '#F59E0B', '#EAB308',
    '#84CC16', '#22C55E', '#10B981', '#14B8A6',
    '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#EC4899', '#F43F5E', '#6B7280',
  ];

  setColor(c: string): void { this.form.patchValue({ color: c }); }

  constructor(private fb: FormBuilder, private api: TaskCatalogApiService) {
    this.initForm();
  }

  ngOnInit(): void {}

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150)]],
      description: ['', [Validators.maxLength(500)]],
      color: ['#2196F3', [Validators.maxLength(30)]],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadItem(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.itemId = id;
    this.api.getCategoryById(id).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          color: item.color ?? '#2196F3',
          isActive: item.isActive ?? true,
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la categoría';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: TaskCategoryRequest = {
      name: v.name.trim(),
      description: v.description?.trim() || undefined,
      color: v.color || undefined,
      isActive: v.isActive ?? true,
    };

    const req$ = this.isEditing && this.itemId
      ? this.api.updateCategory(this.itemId, body)
      : this.api.createCategory(body);

    req$.subscribe({
      next: (item) => { this.saving = false; this.saved.emit(item); },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
      },
    });
  }

  cancelar(): void { this.cancelled.emit(); }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
